// src/components/DashboardAdmin/ICTData/Accommodations.js
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAccommodations,
  insertAccommodation,
  updateAccommodation,
  deleteAccommodation,
} from "../../../store/accommodationSlice";
import { fetchICTData } from "../../../store/uiSlice";
import { fetchEmployees } from "../../../store/empDataSlice";
import { fetchTrainingCards } from "../../../store/trainingCardsSlice";
import { fetchHoursByICTId } from "../../../store/hoursSlice";
import "../../../Styles/Common.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Accommodations = () => {
  const dispatch = useDispatch();

  // Redux state
  const { accommodations, loading, error } = useSelector(
    (state) => state.accommodations
  );
  const { ictData } = useSelector((state) => state.ui);
  const { employees } = useSelector((state) => state.empData);
  const { trainingCards } = useSelector((state) => state.trainingCards);

  // Local state for search, pagination, and form popup.
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Form state for insert/edit.
  // "id" will be blank on insert (auto-generated on backend).
  const [formData, setFormData] = useState({
    id: "",
    accommodationType: "trainer",
    accommodationName: "",
    accommodationFrom: "",
    accommodationTo: "",
    accommodationFor_1: "",
    accommodationFor_2: "",
    tc_id: "",
    accommodationCost: "",
    accommodationBill: null, // Will hold a File object.
    status: "A",
  });

  // Live search state for ICT Name.
  const [ictSearchInput, setIctSearchInput] = useState("");
  const [ictSuggestions, setIctSuggestions] = useState([]);

  // Live search state for TC Id.
  const [tcSearchInput, setTcSearchInput] = useState("");
  const [tcSuggestions, setTcSuggestions] = useState([]);

  const itemsPerPage = 10;

  // Fetch initial data.
  useEffect(() => {
    dispatch(fetchAccommodations());
    dispatch(fetchICTData());
    dispatch(fetchEmployees());
    dispatch(fetchTrainingCards());
  }, [dispatch]);

  // Pre-fill ICT search input if an ICT is already selected.
  useEffect(() => {
    if (formData.ictId_id && ictData.length > 0) {
      const found = ictData.find(
        (ict) => ict.id.toString() === formData.ictId_id.toString()
      );
      if (found) {
        setIctSearchInput(found.ictName);
      }
    }
  }, [formData.ictId_id, ictData]);

  // --- Live search for ICT Name ---
  const handleIctSearchInputChange = (e) => {
    const value = e.target.value;
    setIctSearchInput(value);
    if (value.length >= 3) {
      const suggestions = ictData.filter((ict) =>
        ict.ictName.toLowerCase().includes(value.toLowerCase())
      );
      setIctSuggestions(suggestions);
    } else {
      setIctSuggestions([]);
    }
  };

  const handleSelectIctSuggestion = (ict) => {
    setFormData({ ...formData, ictId_id: ict.id });
    setIctSearchInput(ict.ictName);
    setIctSuggestions([]);
    // Optionally fetch related hours.
    dispatch(fetchHoursByICTId(ict.id));
  };

  // --- Live search for TC Id (min 1 character) ---
  const handleTcSearchInputChange = (e) => {
    const value = e.target.value;
    setTcSearchInput(value);
    if (value.length >= 1) {
      const suggestions = trainingCards.filter((tc) =>
        String(tc.id).includes(value)
      );
      setTcSuggestions(suggestions);
    } else {
      setTcSuggestions([]);
    }
  };

  const handleSelectTcSuggestion = (tc) => {
    setFormData({ ...formData, tc_id: tc.id });
    setTcSearchInput(String(tc.id));
    setTcSuggestions([]);
  };

  // --- General form input change handler ---
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // File upload handler for Accommodation Bill.
  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFormData({ ...formData, accommodationBill: e.target.files[0] });
    }
  };

  // Helper to process formData: remove blank id if not editing and convert numeric fields.
  const processFormData = () => {
    if (editMode) {
      // Include id in update mode.
      return {
        ...formData,
        tc_id: formData.tc_id ? Number(formData.tc_id) : 0,
        accommodationCost: formData.accommodationCost
          ? Number(formData.accommodationCost)
          : 0,
      };
    } else {
      // In insert mode, remove id.
      const { id, ...rest } = formData;
      return {
        ...rest,
        tc_id: rest.tc_id ? Number(rest.tc_id) : 0,
        accommodationCost: rest.accommodationCost ? Number(rest.accommodationCost) : 0,
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const processedData = processFormData();
    console.log("Data to be sent:", processedData);
    try {
      if (editMode) {
        await dispatch(updateAccommodation(processedData)).unwrap();
        toast.success("Accommodation updated successfully!");
      } else {
        await dispatch(insertAccommodation(processedData)).unwrap();
        toast.success("Accommodation added successfully!");
      }
      setIsFormOpen(false);
      setFormData({
        id: "",
        accommodationType: "trainer",
        accommodationName: "",
        accommodationFrom: "",
        accommodationTo: "",
        accommodationFor_1: "",
        accommodationFor_2: "",
        tc_id: "",
        accommodationCost: "",
        accommodationBill: null,
        status: "A",
      });
      setTcSearchInput("");
      dispatch(fetchAccommodations());
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Failed to save accommodation.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this accommodation?")) {
      try {
        await dispatch(deleteAccommodation(id)).unwrap();
        toast.success("Accommodation deleted successfully!");
        dispatch(fetchAccommodations());
      } catch (err) {
        toast.error("Failed to delete accommodation.");
      }
    }
  };

  // Filter accommodations by name.
  const filteredAccommodations = accommodations.filter((acc) =>
    acc.accommodationName?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const totalPages = Math.ceil(filteredAccommodations.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredAccommodations.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="container">
      <div className="header-container">
        <h2>Accommodations</h2>
        <button
          className="add-lead-btn"
          onClick={() => {
            setIsFormOpen(true);
            setEditMode(false);
            setFormData({
              id: "",
              accommodationType: "trainer",
              accommodationName: "",
              accommodationFrom: "",
              accommodationTo: "",
              accommodationFor_1: "",
              accommodationFor_2: "",
              tc_id: "",
              accommodationCost: "",
              accommodationBill: null,
              status: "A",
            });
            setTcSearchInput("");
          }}
        >
          <i className="fa fa-plus"></i>
        </button>
      </div>
      <div className="feedback-filters">
        <input
          type="text"
          placeholder="Search by accommodation"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-input"
        />
      </div>
      {loading && <p>Loading accommodations...</p>}
      {error && (
        <p className="error">
          {typeof error === "object" ? JSON.stringify(error) : error}
        </p>
      )}
      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>Type</th>
            <th>Name</th>
            <th>From</th>
            <th>To</th>
            <th>For (1)</th>
            <th>For (2)</th>
            <th>TC Id</th>
            <th>Cost</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((acc, index) => (
            <tr key={acc.id}>
              <td>{index + 1} #{acc.id}</td>
              <td>{acc.accommodationType}</td>
              <td>
                {acc.accommodationBill ? (
                  <a
                    href={`https://iteyonikservicesinstitute.com/download/documents/accommodations/${acc.accommodationBill.split("/").pop()}`}
                    download
                  >
                    {acc.accommodationName}
                  </a>
                ) : (
                  acc.accommodationName
                )}
              </td>
              <td>{acc.accommodationFrom}</td>
              <td>{acc.accommodationTo}</td>
              <td>{acc.accommodationFor_1}</td>
              <td>{acc.accommodationFor_2}</td>
              <td>{acc.tc_id}</td>
              <td>
                {new Intl.NumberFormat("en-IN", {
                  style: "currency",
                  currency: "INR",
                }).format(acc.accommodationCost)}
              </td>
              <td>
                <button
                  onClick={() => handleDelete(acc.id)}
                  className="icon-btn delete-btn"
                >
                  <i className="fa fa-trash" aria-hidden="true"></i>
                </button>
                <button
                  className="icon-btn update-btn"
                  onClick={() => {
                    setFormData({
                      id: acc.id,
                      accommodationType: acc.accommodationType,
                      accommodationName: acc.accommodationName,
                      accommodationFrom:
                        acc.accommodationFrom?.split("T")[0] || "",
                      accommodationTo: acc.accommodationTo?.split("T")[0] || "",
                      accommodationFor_1: acc.accommodationFor_1,
                      accommodationFor_2: acc.accommodationFor_2,
                      tc_id: acc.tc_id,
                      accommodationCost: acc.accommodationCost,
                      // Do not auto-fill file for update.
                      accommodationBill: null,
                      status: acc.status,
                    });
                    setTcSearchInput(String(acc.tc_id));
                    setEditMode(true);
                    setIsFormOpen(true);
                  }}
                >
                  <i className="fa fa-edit" aria-hidden="true"></i>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {totalPages > 1 && (
        <div className="pagination">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => setCurrentPage(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
      {/* Insert/Edit Form Popup */}
      {isFormOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{editMode ? "Edit Accommodation" : "Add Accommodation"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Accommodation Type</label>
                <select
                  name="accommodationType"
                  value={formData.accommodationType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="trainer">Trainer</option>
                  <option value="learner">Learner</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label>Accommodation Name</label>
                <input
                  type="text"
                  name="accommodationName"
                  placeholder="Accommodation Name"
                  value={formData.accommodationName}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="doubleFormField">
                <div className="form-group halfForm">
                  <label>Accommodation From (Date)</label>
                  <input
                    type="date"
                    name="accommodationFrom"
                    value={formData.accommodationFrom || ""}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group halfForm">
                  <label>Accommodation To (Date)</label>
                  <input
                    type="date"
                    name="accommodationTo"
                    value={formData.accommodationTo || ""}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>
              <div className="doubleFormField">
                <div className="form-group halfForm">
                  <label>Accommodation For 1</label>
                  <input
                    type="text"
                    name="accommodationFor_1"
                    placeholder="First guest/target"
                    value={formData.accommodationFor_1}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group halfForm">
                  <label>Accommodation For 2</label>
                  <input
                    type="text"
                    name="accommodationFor_2"
                    placeholder="Second guest/target (optional)"
                    value={formData.accommodationFor_2}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              {/* Live search for TC Id */}
              <div className="form-group">
                <label>TC Id</label>
                <input
                  type="text"
                  placeholder="Search TC Id"
                  value={tcSearchInput}
                  onChange={handleTcSearchInputChange}
                  required
                />
                {tcSuggestions.length > 0 && (
                  <div className="suggestions-dropdown">
                    {tcSuggestions.map((tc) => (
                      <div
                        key={tc.id}
                        className="suggestion-item"
                        onClick={() => handleSelectTcSuggestion(tc)}
                      >
                        {tc.id}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-group">
                <label>Accommodation Cost</label>
                <input
                  type="number"
                  step="0.01"
                  name="accommodationCost"
                  placeholder="Enter Amount (INR)"
                  value={formData.accommodationCost}
                  onChange={handleInputChange}
                  required
                />
              </div>
              {/* File upload for Accommodation Bill */}
              <div className="form-group">
                <label>Accommodation Bill</label>
                <input
                  type="file"
                  name="accommodationBill"
                  accept=".pdf,.jpg,.jpeg,.doc,.docx"
                  onChange={handleFileChange}
                  required={!editMode}
                />
                {editMode && (
                  <p style={{ fontSize: "0.9em" }}>
                    If you don't select a new file, the old file remains.
                  </p>
                )}
              </div>
              <div className="form-actions">
                <button type="submit">
                  {editMode ? (
                    <i className="fa fa-pencil" aria-hidden="true"></i>
                  ) : (
                    <i className="fa fa-plus-square" aria-hidden="true"></i>
                  )}
                </button>
                <button
                  type="button"
                  className="fa fa-close"
                  onClick={() => setIsFormOpen(false)}
                >
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default Accommodations;
